import { useCallback, useEffect, useState } from 'react';
import type { GameId } from '@/lib/types';

// v6: 5-screen flow. 'home' is the 6-game grid (default). Legacy screen
// names from the old fiction flow still parse → fall back to home.
export type ScreenId = 'home' | 'play' | 'cleared' | 'report';
export type RouteParams = { game?: GameId; demo?: boolean; seed?: number; metrics?: Record<string, number>; mode?: 'full' | 'single' };

const VALID: ScreenId[] = ['home', 'play', 'cleared', 'report'];

function readUrl(): { screen: ScreenId; params: RouteParams } {
  const u = new URL(window.location.href);
  const raw = u.searchParams.get('screen') ?? 'home';
  const screen = (VALID.includes(raw as ScreenId) ? raw : 'home') as ScreenId;
  const params: RouteParams = {};
  const g = u.searchParams.get('game');
  if (g) params.game = g as GameId;
  if (u.searchParams.get('demo') === '1') params.demo = true;
  const mode = u.searchParams.get('mode');
  if (mode === 'full' || mode === 'single') params.mode = mode;
  const seed = u.searchParams.get('seed');
  if (seed) params.seed = Number(seed);
  return { screen, params };
}

function writeUrl(screen: ScreenId, params: RouteParams) {
  const u = new URL(window.location.href);
  u.searchParams.set('screen', screen);
  if (params.game) u.searchParams.set('game', params.game); else u.searchParams.delete('game');
  if (params.demo) u.searchParams.set('demo', '1'); else u.searchParams.delete('demo');
  if (params.mode) u.searchParams.set('mode', params.mode); else u.searchParams.delete('mode');
  if (params.seed != null) u.searchParams.set('seed', String(params.seed)); else u.searchParams.delete('seed');
  window.history.pushState({ screen, params }, '', u.toString());
}

export function useRouter() {
  const [state, setState] = useState(readUrl);

  const navigate = useCallback((screen: ScreenId, next: RouteParams = {}) => {
    writeUrl(screen, next);
    setState({ screen, params: next });
  }, []);

  useEffect(() => {
    const onPop = () => setState(readUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return { ...state, navigate };
}
